// frontend/src/components/daytrader/IntradayLevels.tsx
//
// Intraday reference levels for one symbol: daily VWAP (+1σ/+2σ bands),
// prior-day O/H/L/C, weekly open, Asia range and the session opening range,
// each with its distance from the last price in % and in 1h-ATR units. The
// nearest level above and below price are highlighted. Reference only — the
// levels are where decisions get made, not signals.

import { useCallback, useEffect, useRef, useState } from "react";
import { Layers, RefreshCw } from "lucide-react";
import { Alert, Button, Skeleton, Table, Tooltip } from "@heroui/react";
import { cn } from "@/lib/utils";
import { LEVEL_META, levelLabel, nearestLevels, sortByPriceDesc, type LevelsResponse } from "./dayTrader";

const POLL_MS = 45_000;

export interface IntradayLevelsState {
  data: LevelsResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/** Polls /api/intraday/levels/:symbol every 45s. Shared by the table and the chart overlay. */
export function useIntradayLevels(symbol: string): IntradayLevelsState {
  const [data, setData] = useState<LevelsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    inflight.current?.abort();
    const ctrl = new AbortController();
    inflight.current = ctrl;
    setLoading(true);
    try {
      const res = await fetch(`/api/intraday/levels/${encodeURIComponent(symbol)}`, { signal: ctrl.signal });
      const json = (await res.json()) as LevelsResponse;
      if (!json.success) throw new Error(json.error || "levels unavailable");
      setData(json);
      setError(null);
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      setError(e instanceof Error ? e.message : "Failed to load levels");
    } finally {
      if (inflight.current === ctrl) setLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    setData(null);
    load();
    const id = setInterval(load, POLL_MS);
    return () => { clearInterval(id); inflight.current?.abort(); };
  }, [load]);

  return { data, loading, error, refresh: load };
}

function fmtPrice(n: number): string {
  if (!isFinite(n)) return "—";
  if (n >= 1000) return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(2);
  if (n >= 0.01) return n.toFixed(4);
  return n.toFixed(6);
}
function signed(n: number, digits: number, suffix = ""): string {
  return `${n > 0 ? "+" : ""}${n.toFixed(digits)}${suffix}`;
}

/** One-line explanations shown in the level-name tooltip. */
const LEVEL_HINT: Record<string, string> = {
  vwapUpper2: "Daily VWAP + 2 standard deviations — stretched; mean-reversion zone.",
  vwapUpper1: "Daily VWAP + 1 standard deviation — upper edge of today's fair value.",
  vwap: "Volume-weighted average price since the UTC day open — today's fair value.",
  vwapLower1: "Daily VWAP − 1 standard deviation — lower edge of today's fair value.",
  vwapLower2: "Daily VWAP − 2 standard deviations — stretched; mean-reversion zone.",
  priorDayHigh: "Yesterday's high — liquidity above it; common breakout / sweep level.",
  priorDayOpen: "Yesterday's opening price.",
  priorDayClose: "Yesterday's closing price — where the last UTC day settled.",
  priorDayLow: "Yesterday's low — liquidity below it; common breakdown / sweep level.",
  weeklyOpen: "This week's opening price (Monday 00:00 UTC) — weekly bias pivot.",
  asiaHigh: "High of the Asia session (00–07 UTC); London often raids it.",
  asiaLow: "Low of the Asia session (00–07 UTC); London often raids it.",
  openingRangeHigh: "High of the current session's first 30 minutes.",
  openingRangeLow: "Low of the current session's first 30 minutes.",
};

export function IntradayLevels({ symbol, state, className = "" }: { symbol: string; state: IntradayLevelsState; className?: string }) {
  const { data, loading, error, refresh } = state;
  const rows = data ? sortByPriceDesc(data.distances) : [];
  const nearest = data ? nearestLevels(data.distances) : { above: null, below: null };
  // Index of the first level at/below price: the "price now" divider goes right before it.
  const dividerAt = rows.findIndex(r => r.pct <= 0);
  const asia = data?.levels.asiaRange;
  const or = data?.levels.openingRange;

  // Flatten levels + the "price now" divider into one keyed row list for the Table.
  type Item = { kind: "level"; key: string; r: (typeof rows)[number] } | { kind: "divider"; key: string };
  const items: Item[] = [];
  rows.forEach((r, i) => {
    if (i === dividerAt) items.push({ kind: "divider", key: "__price_now" });
    items.push({ kind: "level", key: r.key, r });
  });
  if (data && dividerAt === -1) items.push({ kind: "divider", key: "__price_now" });

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-separator px-4 py-2 text-2xs text-muted">
        <span className="flex items-center gap-1.5">
          <Layers className="size-3.5" aria-hidden />
          <span className="font-semibold text-foreground">{symbol.replace("USDT", "")}</span> intraday reference levels
        </span>
        {data && (
          <>
            <span>ATR {data.atr.tf}({data.atr.period}) <span className="num text-foreground">{data.atr.value == null ? "—" : fmtPrice(data.atr.value)}</span></span>
            {asia && (
              <span>
                Asia range{asia.complete ? "" : " (forming)"}{" "}
                <span className="num text-foreground">{fmtPrice(asia.high - asia.low)}{data.atr.value ? ` · ${((asia.high - asia.low) / data.atr.value).toFixed(1)} ATR` : ""}</span>
              </span>
            )}
            {or && (
              <span>
                OR 30m{or.complete ? "" : " (forming)"}{" "}
                <span className="num text-foreground">{fmtPrice(or.low)} – {fmtPrice(or.high)}</span>
              </span>
            )}
          </>
        )}
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          className="ml-auto"
          aria-label="Refresh levels (auto every 45s)"
          isDisabled={loading}
          onPress={refresh}
        >
          <RefreshCw className={cn("size-3.5", loading && "animate-spin")} />
        </Button>
      </div>

      {error && (
        <div className="p-3">
          <Alert status={data ? "warning" : "danger"}>
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{data ? "Levels may be stale" : "Levels unavailable"}</Alert.Title>
              <Alert.Description>{error}</Alert.Description>
            </Alert.Content>
            <Button size="sm" variant="secondary" onPress={refresh}>Retry</Button>
          </Alert>
        </div>
      )}

      {!error && !data && (
        <div className="space-y-2 p-4" aria-busy="true" aria-label="Loading levels">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-3 w-28 rounded-md" />
              <Skeleton className="ml-auto h-3 w-20 rounded-md" />
              <Skeleton className="h-3 w-14 rounded-md" />
              <Skeleton className="h-3 w-10 rounded-md" />
            </div>
          ))}
        </div>
      )}

      {data && (
        <Table variant="secondary" className="rounded-none">
          <Table.ScrollContainer className="scrollbar-thin">
            <Table.Content aria-label={`Intraday levels for ${symbol}`} className="min-w-[480px] text-[13px]">
              <Table.Header className="sticky top-0 z-10">
                <Table.Column isRowHeader className={TH}>Level</Table.Column>
                <Table.Column className={cn(TH, "text-right")}>Price</Table.Column>
                <Table.Column className={cn(TH, "text-right")}>Dist %</Table.Column>
                <Table.Column className={cn(TH, "text-right")}>
                  <span title={`Distance in ${data.atr.tf} ATR(${data.atr.period}) units`}>ATR</span>
                </Table.Column>
              </Table.Header>
              <Table.Body
                items={items}
                renderEmptyState={() => (
                  <div className="py-6 text-center text-xs text-muted">No levels yet for today.</div>
                )}
              >
                {(item: Item) => {
                  if (item.kind === "divider") {
                    return (
                      <Table.Row id={item.key} className="bg-accent-soft">
                        <Table.Cell className={cn(TD, "text-2xs font-semibold uppercase tracking-wide text-accent-soft-foreground")}>Price now</Table.Cell>
                        <Table.Cell className={cn(TD, "num text-right font-semibold text-accent-soft-foreground")}>{fmtPrice(data.lastPrice)}</Table.Cell>
                        <Table.Cell className={TD}>{""}</Table.Cell>
                        <Table.Cell className={TD}>{""}</Table.Cell>
                      </Table.Row>
                    );
                  }
                  const r = item.r;
                  const isAbove = r.key === nearest.above;
                  const isBelow = r.key === nearest.below;
                  const meta = LEVEL_META[r.key];
                  return (
                    <Table.Row id={r.key}>
                      <Table.Cell className={TD}>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-0.5 w-3 rounded-full" style={{ background: meta ? `var(${meta.color})` : "currentColor" }} aria-hidden />
                          <Tooltip delay={250}>
                            <Tooltip.Trigger aria-label={`${levelLabel(r.key)} — what is this level?`}>
                              <span className={cn("cursor-help underline decoration-dotted decoration-muted/50 underline-offset-2", isAbove || isBelow ? "font-semibold text-foreground" : "text-foreground/85")}>
                                {levelLabel(r.key)}
                              </span>
                            </Tooltip.Trigger>
                            <Tooltip.Content className="max-w-64 text-xs">{LEVEL_HINT[r.key] ?? levelLabel(r.key)}</Tooltip.Content>
                          </Tooltip>
                          {isAbove && <span className="text-3xs text-loss" title="Nearest level above price">▲ next</span>}
                          {isBelow && <span className="text-3xs text-profit" title="Nearest level at/below price">▼ next</span>}
                        </span>
                      </Table.Cell>
                      <Table.Cell className={cn(TD, "num text-right text-foreground")}>{fmtPrice(r.price)}</Table.Cell>
                      <Table.Cell className={cn(TD, "num text-right", r.pct > 0 ? "text-loss" : "text-profit")}>{signed(r.pct, 2, "%")}</Table.Cell>
                      <Table.Cell className={cn(TD, "num text-right text-muted")}>{r.atr == null ? "—" : signed(r.atr, 1)}</Table.Cell>
                    </Table.Row>
                  );
                }}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      )}

      {data && (
        <p className="border-t border-separator px-4 py-2 text-2xs text-muted">
          Reference levels only — not entries. Red = resistance-side (above price), green = support-side.
        </p>
      )}
    </div>
  );
}

const TH = "px-3 py-2 text-2xs font-medium uppercase tracking-wide";
const TD = "px-3 py-1.5";
